import axios from 'axios';
import { API_URL } from '../config';

const api = axios.create({
  baseURL: API_URL,
});

// Intercepteur pour ajouter le token JWT et gérer Content-Type
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Ne pas définir Content-Type pour FormData (le navigateur le fait automatiquement)
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }
  
  return config;
});

// Intercepteur pour gérer les comptes suspendus/supprimés
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      const accountStatus = error.response?.data?.account_status;
      
      if (accountStatus === 'suspended' || accountStatus === 'deleted') {
        // Stocker le message pour l'afficher sur la page de login
        localStorage.setItem('account_error', error.response?.data?.error);
        localStorage.removeItem('token');
        
        // Rediriger vers login seulement si pas déjà sur cette page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// API Annonces administrateur
export const announcementAPI = {
  // Récupérer les annonces non lues (pour tous les utilisateurs)
  getUnread: () => api.get('/announcements/unread'),
  
  // Marquer une annonce comme lue
  markAsRead: (id) => api.post(`/announcements/${id}/read`),
  
  // Admin: Récupérer toutes les annonces
  getAll: () => api.get('/announcements'),
  
  // Admin: Créer une annonce
  create: (data) => api.post('/announcements', data),
  
  // Admin: Récupérer une annonce
  getById: (id) => api.get(`/announcements/${id}`),
  
  // Admin: Mettre à jour une annonce
  update: (id, data) => api.put(`/announcements/${id}`, data),
  
  // Admin: Supprimer une annonce
  delete: (id) => api.delete(`/announcements/${id}`),
  
  // Admin: Désactiver une annonce
  deactivate: (id) => api.put(`/announcements/${id}/deactivate`),
};

// API Portefeuille Plateforme
export const walletAPI = {
  // Récupérer le portefeuille avec les dernières transactions
  get: () => api.get('/wallet'),
  
  // Récupérer uniquement le solde
  getBalance: () => api.get('/wallet/balance'),
  
  // Récupérer l'historique des transactions
  getTransactions: (params = {}) => api.get('/wallet/transactions', { params }),
  
  // Créer une demande de dépôt
  createDepositRequest: (data) => api.post('/wallet/deposit-request', data),
  
  // Récupérer ses demandes de dépôt
  getDepositRequests: (status = null) => api.get('/wallet/deposit-requests', { params: { status } }),
  
  // Payer une commission de projet depuis le portefeuille
  payCommission: (projectId, amount) => api.post(`/wallet/pay-commission/${projectId}`, { amount }),
  
  // Admin: Liste de tous les portefeuilles
  adminList: () => api.get('/admin/wallets'),
  
  // Admin: Demandes de dépôt en attente
  adminGetPendingDeposits: () => api.get('/admin/wallets/pending-deposits'),
  
  // Admin: Approuver un dépôt
  adminApproveDeposit: (requestId) => api.post(`/admin/wallets/approve-deposit/${requestId}`),
  
  // Admin: Rejeter un dépôt
  adminRejectDeposit: (requestId, reason) => api.post(`/admin/wallets/reject-deposit/${requestId}`, { reason }),
  
  // Admin: Dépôt direct
  adminDeposit: (userId, amount, description) => api.post(`/admin/wallets/deposit/${userId}`, { amount, description }),
  
  // Admin: Remboursement
  adminRefund: (userId, amount, description) => api.post(`/admin/wallets/refund/${userId}`, { amount, description }),
};

export default api;
